<template>
  <div class="chat-call-options">
    <div class="form-input">
      <MicInput
        v-model.trim="formdata.phone"
        v-validate="'required|phoneNumber'"
        :required="true"
        name="Phone"
        type="tel"
        label="Best phone number to call"
        :placeholder="whitelabelPhone"
        :error="errors.first('Phone')"
        :is-valid="isPhoneValid()"
      />
      <span class="phone-disclaimer-text">Only a Bionic expert will contact you</span>
    </div>
    <div class="form-input">
      <MicInput
        v-model.trim="formdata.name"
        v-validate="'fullName'"
        :required="true"
        name="Name"
        label="Your full name"
        :placeholder="whitelabelFullName"
        :error="errors.first('Name')"
        :is-valid="isNameValid()"
      />
    </div>
    <div class="form-input">
      <MicInput
        v-model.trim="formdata.business"
        v-validate="'required|max:99|businessName'"
        :required="true"
        name="Business name"
        label="Business name"
        :placeholder="whitelabelBusinessName"
        :error="errors.first('Business name')"
        :is-valid="isBusinessNameValid()"
      />
    </div>
    <div class="form-input">
      <MicInput
        v-model.trim="formdata.email"
        v-validate="'required|email'"
        :required="true"
        name="Email"
        label="Best email to send quotes to"
        :placeholder="whitelabelEmail"
        :error="errors.first('Email')"
        :is-valid="isEmailValid()"
      />
    </div>
    <CDCCallTimeForm v-if="!isOfficeOpen()" />

    <div class="form-footer">
      <MicButton
        :compact="true"
        class="submit-btn"
        :disabled="isSubmitDisabled"
        @click="submit"
      >
        {{ isScheduleCall ? scheduleCallText : callNowText }}
        <img
          v-if="buttonIcon"
          :src="require(`@mic/frontend/assets/icons/${buttonIcon}`)"
          alt="button icon"
          class="next-btn__icon"
        >
      </MicButton>
    </div>

    <div class="tp-container">
      <div v-if="isOfficeOpen()" class="connect-text">
        Connect in 1-3 minutes
      </div>
      <!-- TrustBox widget - Mini -->
      <div
        id="trustbox"
        class="trustpilot-widget"
        data-locale="en-GB"
        data-template-id="53aa8807dec7e10d38f59f32"
        data-businessunit-id="5194a5e800006400052ec840"
        data-style-height="80px"
        data-style-width="80px"
        data-theme="light"
        data-stars="1,2,3,4,5"
        data-no-reviews="hide"
        data-scroll-to-list="true"
        data-allow-robots="true"
      >
        <a href="https://uk.trustpilot.com/review/bionic.co.uk" target="_blank" rel="noopener">Trustpilot</a>
      </div>
      <!-- End TrustBox widget -->
    </div>
  </div>
</template>

<script>
import MicButton from '../buttons/MicButton'
import MicInput from '../inputs/MicInput'
import InputRegexes from '../mixins/InputRegexes'
import Leadable from '../mixins/Leadable'
import CDCCallTimeForm from './CDCCallTimeForm'
import IsOfficeOpen from '../mixins/IsOfficeOpen'

export default {
  components: {
    MicButton,
    MicInput,
    CDCCallTimeForm
  },
  mixins: [InputRegexes, Leadable, IsOfficeOpen],
  props: {
    message: {
      type: Object,
      required: true
    },
    isScheduleCall: {
      type: Boolean,
      default: false
    },
    buttonIcon: {
      type: String,
      default: ''
    },
    callNowText: {
      type: String,
      default: 'Call me'
    },
    scheduleCallText: {
      type: String,
      default: 'Schedule call'
    },
    chatPageDisclaimer: {
      type: String,
      default: ''
    },
    chatPageDisclaimerLinkIcon: {
      type: String,
      default: ''
    },
    whitelabelBusinessName: {
      type: String,
      default: 'Business name'
    },
    whitelabelFullName: {
      type: String,
      default: 'First and Last name'
    },
    whitelabelPhone: {
      type: String,
      default: 'Mobile number is best'
    },
    whitelabelEmail: {
      type: String,
      default: 'Email address'
    }
  },
  data () {
    return {
      formdata: {
        business: '',
        name: '',
        phone: '',
        email: ''
      },
      dict: {
        custom: {
          'Business name': {
            required: 'Please enter your business name',
            businessName: 'Please enter a valid business name'
          },
          Name: {
            required: 'Please enter your full name',
            fullName: 'Please enter first and last name'
          },
          Phone: {
            required: 'Please enter a valid phone number',
            phoneNumber: 'Please enter a valid phone number'
          },
          Email: {
            required: 'Please enter a valid email address',
            email: 'Please enter a valid email address'
          }
        }
      }
    }
  },
  computed: {
    isSubmitDisabled () {
      return (
        !this.formdata.business ||
        !this.formdata.name ||
        !this.formdata.phone ||
        !this.formdata.email ||
        this.errors.items.length !== 0
      )
    },
    disclaimerLinkBackground () {
      const iconLink = this.chatPageDisclaimerLinkIcon
        ? require(`../assets/icons/${this.chatPageDisclaimerLinkIcon}`)
        : ''
      return `--discalimer-link-background: url(${iconLink})`
    }
  },
  mounted () {
    const trustboxRef = document.getElementById('trustbox')
    window.Trustpilot.loadFromElement(trustboxRef)
  },
  created () {
    this.$validator.extend('businessName', {
      validate: (value, field) => {
        return this.testBusinessNameRegex()
      }
    })

    this.$validator.extend('fullName', {
      validate: (value, field) => {
        return this.isNameValid()
      }
    })

    this.$validator.extend('phoneNumber', {
      validate: (value, field) => {
        return this.isPhoneValid()
      }
    })

    this.$validator.localize('en', this.dict)
  },
  methods: {
    hasError (name) {
      return Boolean(this.errors.items.find((err) => err.name === name))
    },
    isBusinessNameValid () {
      return (
        Boolean(this.formdata.business) &&
        this.formdata.business.length <= 99 &&
        this.testBusinessNameRegex()
      )
    },
    isNameValid () {
      return (
        Boolean(this.formdata.name) &&
        this.formdata.name.length <= 30 &&
        Boolean(this.formdata.name.match(this.fullNameRegex))
      )
    },
    isPhoneValid () {
      return (
        Boolean(this.formdata.phone) &&
        Boolean(this.formdata.phone.match(this.phoneRegex))
      )
    },
    isEmailValid () {
      return (
        Boolean(this.formdata.email) &&
        Boolean(this.formdata.email.match(this.emailRegex))
      )
    },
    testBusinessNameRegex () {
      return Boolean(this.formdata.business.match(this.businessNameRegex))
    },
    submit () {
      this.submitLead('telco', {
        businessName: this.formdata.business,
        telephoneNumber: this.formdata.phone,
        email: this.formdata.email,
        name: this.formdata.name,
        reviewTelco: true
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.form-input {
  margin-bottom: $spacing-20;
  width: 380px;
  max-width: 100%;
  position: relative;

  .phone-disclaimer-text {
    color: #5a5a5a;
    font-size: 14px;
    letter-spacing: -0.56px;
    line-height: 16px;
    text-align: left;
    display: block;
    margin-top: 6px;
  }

  label {
    display: block;
    font-size: 16px;
    letter-spacing: 0;
    line-height: 20px;
    font-weight: bold;
    text-align: left;
  }

  input {
    padding: $spacing-16;
    border-radius: $border-radius-small;
    background-color: $white;
    border: 1px solid $chat-bot-select-border;
    width: 100%;

    @media (max-width: $screen-md) {
      font-size: 14px;
      line-height: 17px;
      padding: $spacing-12 $spacing-8;
    }
  }
}

.tp-container {
  display: flex;
  align-items: center;

  .connect-text {
    color: $bionic-navy;
    font-size: 12px;
    letter-spacing: 0;
    line-height: 15px;
  }

  .trustpilot-widget {
    width: 80px;
    margin: 16px 0 0 auto;
  }
}

.form-footer {
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 446px;
  align-items: center;

  .submit-btn {
    flex-grow: 1;
    padding: $spacing-0 $spacing-20;

    @media (min-width: 800px) {
      font-size: 18px;
    }
  }

  @media (max-width: $screen-sm) {
    flex-direction: column;

    .submit-btn {
      min-width: 100%;
      font-size: 14px;
      font-weight: bold;
      line-height: 24px;
      padding: $spacing-0 $spacing-11;
    }
  }
}

.form-disclaimer {
  display: flex;
  align-items: center;
  margin-bottom: $spacing-24;

  .lock {
    min-width: 16px;
    width: 16px;
    opacity: 0.5;
    margin-right: $spacing-16;
  }

  .disclaimer-text {
    font-size: 14px;
    line-height: 16px;
    opacity: 0.8;
  }

  @media (min-width: $screen-sm) {
    max-width: 232px;
    margin: 0 $spacing-16 0 0;
  }
}

.next-btn {
  &__icon {
    margin-left: $spacing-8;
  }
}
</style>
